import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Loader2, Facebook, Sparkles, CheckCircle2, AlertTriangle, CalendarClock, History } from "lucide-react";

const TIER_STYLE = {
  hot: { label: "Chaud", color: "#f43f5e", bg: "rgba(244,63,94,0.12)" },
  warm: { label: "Tiède", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  cold: { label: "Froid", color: "#38bdf8", bg: "rgba(56,189,248,0.12)" },
};

export const MetaLeadsDialog = ({ open, onClose, api, onImported }) => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [simulate, setSimulate] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [creatingProcess, setCreatingProcess] = useState(false);
  const [processMsg, setProcessMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setProcessMsg("");
    (async () => {
      try {
        const [s, h] = await Promise.all([api.get("/meta/leads/status"), api.get("/meta/leads/history")]);
        setStatus(s.data);
        setHistory(h.data.syncs || []);
        setSimulate(!s.data.connected);
      } catch (e) { /* silencieux */ }
    })();
  }, [open]);

  const runSync = async () => {
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/meta/leads/sync", { simulate });
      setResult(r.data);
      if (r.data.imported > 0) onImported?.();
      const h = await api.get("/meta/leads/history");
      setHistory(h.data.syncs || []);
    } catch (e) {
      setResult({ errors: [e.response?.data?.detail || "Erreur lors de l'import"], imported: 0, duplicates: 0, details: [] });
    } finally {
      setRunning(false);
    }
  };

  const createDailyProcess = async () => {
    setCreatingProcess(true);
    setProcessMsg("");
    try {
      await api.post("/ai/processes", {
        name: "Import quotidien des leads Meta",
        agent_key: "vente",
        schedule_type: "daily",
        schedule_time: "08:00",
        allow_actions: true,
        notify: true,
        instructions:
          "Chaque matin, importe les nouveaux leads Meta Lead Ads, qualifie-les (score, priorité chaud/tiède/froid) et ajoute-les au CRM en déclenchant l'action sync_meta_leads" +
          (simulate ? ' avec {"simulate": true} (mode démonstration tant que Meta n\'est pas connecté)' : "") +
          ". Termine par un compte-rendu : nombre de leads importés, doublons ignorés, et liste des leads chauds à rappeler en priorité aujourd'hui.",
      });
      setProcessMsg("Process quotidien créé (Agent Force de Vente, 08:00) — visible dans la page Agent IA.");
    } catch (e) {
      setProcessMsg(e.response?.data?.detail || "Erreur lors de la création du process");
    } finally {
      setCreatingProcess(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="meta-leads-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Facebook className="w-5 h-5 text-[#1877f2]" /> Leads Meta — import & qualification IA
          </DialogTitle>
        </DialogHeader>

        {/* Statut connexion */}
        <div className="rounded-lg border border-white/10 p-3 text-sm" data-testid="meta-leads-status">
          {status === null ? (
            <span className="text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</span>
          ) : status.connected ? (
            <span className="text-emerald-400 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Meta connecté — {status.pages.length} page(s) : {status.pages.map(p => p.name).join(", ")}
            </span>
          ) : (
            <span className="text-amber-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Aucune page Meta connectée (Marketing → Réseaux Sociaux). Le mode simulation permet de tester tout le flux.
            </span>
          )}
          {status?.imported_total > 0 && (
            <p className="text-xs text-slate-500 mt-1">{status.imported_total} prospect(s) déjà importé(s) depuis Meta Lead Ads.</p>
          )}
        </div>

        {/* Simulation toggle */}
        <div className="flex items-center justify-between rounded-lg border border-white/10 p-3">
          <div>
            <p className="text-sm text-white flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#f7c935]" /> Mode simulation (leads de démonstration)</p>
            <p className="text-xs text-slate-500 mt-0.5">Génère des leads fictifs réalistes pour valider la qualification IA et l'import CRM.</p>
          </div>
          <Switch checked={simulate} onCheckedChange={setSimulate} data-testid="meta-leads-simulate-toggle" />
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button onClick={runSync} disabled={running || (!simulate && status && !status.connected)}
            className="bg-[#1877f2] text-white hover:bg-[#1462c9] font-bold" data-testid="meta-leads-run-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Facebook className="w-4 h-4 mr-1.5" />}
            {running ? "Import & qualification en cours…" : "Importer les leads maintenant"}
          </Button>
          <Button onClick={createDailyProcess} disabled={creatingProcess} variant="outline"
            className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="meta-leads-create-process-btn">
            {creatingProcess ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-1.5" />}
            Créer le process quotidien (08:00)
          </Button>
        </div>
        {processMsg && <p className="text-xs text-[#f7c935]" data-testid="meta-leads-process-msg">{processMsg}</p>}

        {/* Résultats */}
        {result && (
          <div className="rounded-lg border border-white/10 p-3 space-y-2" data-testid="meta-leads-results">
            <p className="text-sm font-bold text-white">
              {result.imported} lead(s) importé(s) et qualifié(s) · {result.duplicates} doublon(s) ignoré(s)
            </p>
            {(result.errors || []).map((e, i) => (
              <p key={i} className="text-xs text-rose-400 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> {e}</p>
            ))}
            {(result.details || []).map((d) => {
              const ts = TIER_STYLE[d.tier] || TIER_STYLE.cold;
              return (
                <div key={d.prospect_id} className="flex items-center gap-2 text-sm py-1.5 border-t border-white/5" data-testid={`meta-lead-row-${d.prospect_id}`}>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ color: ts.color, background: ts.bg }}>
                    {ts.label} · {d.score}
                  </span>
                  <span className="text-white font-medium">{d.name}</span>
                  {d.company && <span className="text-slate-500 text-xs">{d.company}</span>}
                  <span className="text-slate-400 text-xs truncate flex-1">{d.headline}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Historique */}
        {history.length > 0 && (
          <div className="rounded-lg border border-white/10 p-3" data-testid="meta-leads-history">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
              <History className="w-3.5 h-3.5" /> Dernières synchronisations
            </p>
            {history.slice(0, 5).map((s) => (
              <div key={s.sync_id} className="flex items-center gap-2 text-xs text-slate-400 py-1 border-t border-white/5">
                <span>{new Date(s.created_at).toLocaleString("fr-FR")}</span>
                <span className="text-slate-500">· {s.trigger === "process" ? "Process IA" : "Manuel"}{s.simulate ? " (simulation)" : ""}</span>
                <span className="ml-auto text-white">{s.imported} importé(s)</span>
                <span>· {s.duplicates} doublon(s)</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MetaLeadsDialog;
